import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonBase, Popover, Tooltip } from '@mui/material';

const MARKER = 'KAIROS_COMPACT_LAYOUT_TABLE_V1';

const RectangularLayoutGlyph = ({ layout, width = 16, height = 12 }) => {
  const rows = Math.max(1, Number(layout?.rows || 1));
  const cols = Math.max(1, Number(layout?.cols || 1));
  return (
    <Box
      aria-hidden="true"
      sx={{
        width,
        height,
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: '1px',
        flex: '0 0 auto',
      }}
    >
      {Array.from({ length: Math.min(9, rows * cols) }).map((_, index) => (
        <Box key={index} sx={{ border: '1px solid currentColor', borderRadius: '1px', opacity: 0.9 }} />
      ))}
    </Box>
  );
};

const MixedLayoutGlyph = ({ layout }) => {
  if (layout?.presetId === '2-over-3') {
    return (
      <Box aria-hidden="true" sx={{ width: 28, height: 18, display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: '1px' }}>
        {[{ gridColumn: 'span 3' }, { gridColumn: 'span 3' }, { gridColumn: 'span 2' }, { gridColumn: 'span 2' }, { gridColumn: 'span 2' }].map((sx, index) => <Box key={index} sx={{ ...sx, border: '1px solid currentColor' }} />)}
      </Box>
    );
  }
  return (
    <Box aria-hidden="true" sx={{ width: 28, height: 18, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, 1fr)', gap: '1px' }}>
      {[0, 1, 2, 3].map(index => <Box key={`block-${index}`} sx={{ gridColumn: index % 2 === 0 ? '1' : '2', gridRow: Math.floor(index / 2) + 1, border: '1px solid currentColor' }} />)}
      {[0, 1, 2].map(index => <Box key={`strip-${index}`} sx={{ gridColumn: '3', gridRow: index + 1, border: '1px solid currentColor' }} />)}
    </Box>
  );
};

const LayoutGlyph = ({ layout, width, height }) => layout?.presetId
  ? <MixedLayoutGlyph layout={layout} />
  : <RectangularLayoutGlyph layout={layout} width={width} height={height} />;

export default function LayoutGridPicker({ layouts = [], activeLayout, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredDimensions, setHoveredDimensions] = useState(null);
  const regularLayouts = useMemo(() => layouts.filter(layout => !layout.presetId && layout.rows <= 4 && layout.cols <= 4), [layouts]);
  const mixedLayouts = useMemo(() => layouts.filter(layout => Boolean(layout.presetId)), [layouts]);
  const resolvedActiveLayout = activeLayout || regularLayouts[0] || layouts[0] || { label: '1×1', rows: 1, cols: 1 };
  const activeDimensions = resolvedActiveLayout.presetId ? null : { rows: resolvedActiveLayout.rows || 1, cols: resolvedActiveLayout.cols || 1 };
  const highlightedDimensions = hoveredDimensions || activeDimensions;

  const closePicker = () => {
    setAnchorEl(null);
    setHoveredDimensions(null);
  };

  const chooseLayout = layout => {
    onChange(null, layout.label);
    closePicker();
  };

  return (
    <>
      <Tooltip title="Choose viewport layout">
        <Button
          aria-label={`Viewport layout ${resolvedActiveLayout.label}`}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchorEl)}
          onClick={event => setAnchorEl(event.currentTarget)}
          sx={{
            minWidth: 0,
            minHeight: 24,
            px: 0.65,
            py: 0,
            gap: 0.45,
            color: '#c6f6ff',
            border: '1px solid rgba(110,239,255,0.30)',
            bgcolor: 'rgba(110,239,255,0.07)',
            fontSize: 10,
            lineHeight: 1,
          }}
        >
          <LayoutGlyph layout={resolvedActiveLayout} width={15} height={12} />
          <Box component="span">{resolvedActiveLayout.label}</Box>
          <Box component="span" aria-hidden="true" sx={{ ml: 0.05, fontSize: 8, lineHeight: 1 }}>▾</Box>
        </Button>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          role: 'dialog',
          'aria-label': 'Choose viewport layout',
          sx: {
            mt: 0.25,
            p: '4px',
            bgcolor: '#071117',
            border: '1px solid rgba(110,239,255,0.28)',
            borderRadius: 1,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          role="group"
          aria-label="Viewport layout grid"
          data-layout-grid-picker={MARKER}
          onMouseLeave={() => setHoveredDimensions(null)}
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 24px)', gridTemplateRows: 'repeat(4, 24px)', gap: 0 }}
        >
          {regularLayouts.map(layout => {
            const highlighted = Boolean(highlightedDimensions && layout.rows <= highlightedDimensions.rows && layout.cols <= highlightedDimensions.cols);
            const selected = layout.label === resolvedActiveLayout.label;
            return (
              <ButtonBase
                key={layout.label}
                aria-label={`Use ${layout.rows} by ${layout.cols} viewport layout`}
                aria-pressed={selected}
                data-highlighted={String(highlighted)}
                onMouseEnter={() => setHoveredDimensions({ rows: layout.rows, cols: layout.cols })}
                onFocus={() => setHoveredDimensions({ rows: layout.rows, cols: layout.cols })}
                onClick={() => chooseLayout(layout)}
                sx={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  borderRadius: 0,
                  borderTop: '1px solid rgba(110,239,255,0.30)',
                  borderLeft: '1px solid rgba(110,239,255,0.30)',
                  borderRight: layout.cols === 4 ? '1px solid rgba(110,239,255,0.30)' : 0,
                  borderBottom: layout.rows === 4 ? '1px solid rgba(110,239,255,0.30)' : 0,
                  bgcolor: highlighted ? 'rgba(110,239,255,0.24)' : 'rgba(110,239,255,0.035)',
                  color: selected ? '#6eefff' : '#8aa8b8',
                  boxShadow: selected ? 'inset 0 0 0 2px #6eefff' : 'none',
                  '&:hover, &:focus-visible': { bgcolor: 'rgba(110,239,255,0.31)' },
                }}
              />
            );
          })}
        </Box>
        {mixedLayouts.length > 0 && (
          <Box role="group" aria-label="Mixed viewport layouts" sx={{ display: 'grid', gridTemplateColumns: `repeat(${mixedLayouts.length}, 1fr)`, gap: '2px', mt: '4px' }}>
            {mixedLayouts.map(layout => (
              <Tooltip key={layout.label} title={layout.label} placement="bottom">
                <ButtonBase
                  aria-label={`Use ${layout.label} viewport layout`}
                  aria-pressed={layout.label === resolvedActiveLayout.label}
                  data-layout-preset={layout.presetId}
                  onClick={() => chooseLayout(layout)}
                  sx={{
                    minWidth: 0,
                    width: '100%',
                    height: 24,
                    color: layout.label === resolvedActiveLayout.label ? '#6eefff' : '#8aa8b8',
                    bgcolor: layout.label === resolvedActiveLayout.label ? 'rgba(110,239,255,0.20)' : 'rgba(110,239,255,0.05)',
                    border: '1px solid rgba(110,239,255,0.24)',
                    borderRadius: '2px',
                    '&:hover, &:focus-visible': { bgcolor: 'rgba(110,239,255,0.22)', color: '#c6f6ff' },
                  }}
                >
                  <LayoutGlyph layout={layout} />
                </ButtonBase>
              </Tooltip>
            ))}
          </Box>
        )}
      </Popover>
    </>
  );
}
